/** Preferenze utente (API + fallback localStorage). */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";
import { endpoints } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/input";
import { CenteredLoader } from "@/components/ui/feedback";
import { ChoiceSelect } from "@/components/forms";
import { TIPO_MOVIMENTO } from "@/lib/rentriChoices";

const PREFS_KEY = "rentri.userPreferences";

export type UserPreferences = {
  movimento_tipo_default?: "Carico" | "Scarico";
  registro_mostra_inviati?: boolean;
  avanzato_aperto?: boolean;
  formulari_tab_default?: "digitali" | "cartacei";
  dashboard_alert_giacenze?: boolean;
};

function loadLocal(): UserPreferences {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? (JSON.parse(raw) as UserPreferences) : {};
  } catch {
    return {};
  }
}

export function PreferenzeOperative() {
  const qc = useQueryClient();
  const [prefs, setPrefs] = useState<UserPreferences>(loadLocal);

  const remoteQ = useQuery({
    queryKey: ["user-preferences"],
    queryFn: endpoints.settings.preferences,
    retry: false,
  });

  useEffect(() => {
    if (remoteQ.data?.preferences && typeof remoteQ.data.preferences === "object") {
      setPrefs((p) => ({ ...p, ...(remoteQ.data.preferences as UserPreferences) }));
    }
  }, [remoteQ.data]);

  const saveMut = useMutation({
    mutationFn: async (next: UserPreferences) => {
      localStorage.setItem(PREFS_KEY, JSON.stringify(next));
      try {
        await endpoints.settings.savePreferences(next);
      } catch {
        /* fallback solo locale */
      }
    },
    onSuccess: () => {
      toast.success("Preferenze salvate");
      qc.invalidateQueries({ queryKey: ["user-preferences"] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Errore salvataggio"),
  });

  const patch = (partial: UserPreferences) => {
    const next = { ...prefs, ...partial };
    setPrefs(next);
    saveMut.mutate(next);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Settings2 className="h-4 w-4" /> Preferenze operative
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {remoteQ.isLoading && <CenteredLoader />}
        <div>
          <Label>Tipo movimento predefinito</Label>
          <div className="mt-1 max-w-xs">
            <ChoiceSelect
              value={prefs.movimento_tipo_default ?? "Carico"}
              onChange={(v) =>
                patch({ movimento_tipo_default: (v || "Carico") as "Carico" | "Scarico" })
              }
              options={TIPO_MOVIMENTO}
              required
              showDescription={false}
            />
          </div>
        </div>
        <div>
          <Label>Tab predefinita in Formulari</Label>
          <div className="mt-1 max-w-xs">
            <ChoiceSelect
              value={prefs.formulari_tab_default ?? "digitali"}
              onChange={(v) =>
                patch({
                  formulari_tab_default: (v === "cartacei" ? "cartacei" : "digitali"),
                })
              }
              options={[
                { value: "digitali", label: "Digitali" },
                { value: "cartacei", label: "Cartacei" },
              ]}
              required
              showDescription={false}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={prefs.registro_mostra_inviati !== false}
            onChange={(e) => patch({ registro_mostra_inviati: e.target.checked })}
          />
          Nel registro, mostra per default i movimenti inviati
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={prefs.avanzato_aperto === true}
            onChange={(e) => patch({ avanzato_aperto: e.target.checked })}
          />
          Apri &quot;Strumenti avanzati&quot; nel menu laterale
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={prefs.dashboard_alert_giacenze !== false}
            onChange={(e) => patch({ dashboard_alert_giacenze: e.target.checked })}
          />
          Mostra avvisi giacenze elevate in Home
        </label>
        <Button
          variant="outline"
          size="sm"
          onClick={() => saveMut.mutate(prefs)}
          disabled={saveMut.isPending}
        >
          Salva preferenze
        </Button>
      </CardContent>
    </Card>
  );
}

export function getUserPreferences(): UserPreferences {
  return loadLocal();
}
